// Integer arithmetic shared by the money functions (modules-and-api.md §2.6). Internal to the package: callers use the named
// functions of `index.ts`, never these helpers, so every rounding a document shows is one of the package's own.

/** A non-integer amount, rate or quantity is a programming error: `TypeError`, as `exVat` and `apportion` have it. */
export function assertInt(n: number, what: string): void {
  if (!Number.isInteger(n)) throw new TypeError(`${what} must be an integer, got ${n}`);
}

/** An integer beyond ±2^53 − 1 cannot be held exactly in a JavaScript number: `RangeError`. */
export function assertSafe(n: number, what: string): void {
  if (!Number.isSafeInteger(n)) throw new RangeError(`${what} is beyond the safe integer range`);
}

/** A value below its floor is outside the function's range: `RangeError`. */
export function assertAtLeast(n: number, min: number, what: string): void {
  if (n < min) throw new RangeError(`${what} must be at least ${min}, got ${n}`);
}

/** `-0` never leaves the package. */
export const noNegativeZero = (n: number): number => (n === 0 ? 0 : n);

/**
 * Quotient and remainder of two non-negative safe integers, exact: the floating division is only an estimate, corrected by
 * the remainder, so a quotient near an integer boundary is never off by one.
 */
function divMod(n: number, d: number): [q: number, r: number] {
  let q = Math.floor(n / d);
  let r = n - q * d;
  while (r < 0) { q -= 1; r += d; }
  while (r >= d) { q += 1; r -= d; }
  return [q, r];
}

/** ⌊n ÷ d⌋ for a non-negative numerator and a positive denominator. */
export function floorDiv(numerator: number, denominator: number): number {
  return divMod(numerator, denominator)[0];
}

/** Round half-up a rational given as numerator/denominator, symmetric about zero: 0.5 → 1, −0.5 → −1. */
export function roundHalfUp(numerator: number, denominator: number): number {
  const sign = Math.sign(numerator) * Math.sign(denominator) || 1;
  const [q, r] = divMod(Math.abs(numerator), Math.abs(denominator));
  return noNegativeZero(sign * (r * 2 >= Math.abs(denominator) ? q + 1 : q));
}
